// Announcement Modal Component
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiUpload, FiImage } from 'react-icons/fi';
import { Announcement, AnnouncementType, MediaType } from '../../../../../../types/announcement';
import Button from '../../../../../../components/ui/Button';
import InputField from '../../../../../../components/ui/InputField';
import Select from '../../../../../../components/ui/Select';
import { uploadToSupabaseStorage } from '../../../../../../utils/supabaseStorage';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (announcement: Partial<Announcement>) => void;
  announcement?: Announcement | null;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  isOpen,
  onClose,
  onSave,
  announcement,
}) => {
  const { t } = useTranslation();

  // Arabic type mapping
  const arabicTypeMap: Record<AnnouncementType, string> = {
    info: 'معلومات',
    sale: 'تخفيض',
    gift: 'هدية',
    event: 'حدث',
    promo: 'عرض',
  };

  // Arabic media type mapping
  const arabicMediaTypeMap: Record<MediaType, string> = {
    text: 'نص',
    image: 'صورة',
    video: 'فيديو',
    none: '',
  };

  const [formData, setFormData] = useState<Partial<Announcement>>({
    type: 'info',
    media_type: 'text',
    content: { title: '', description: '' },
    ar: { type: 'معلومات', content: { title: '', description: '' }, media_type: 'نص' },
    start_at: '',
    end_at: '',
    priority: 0,
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (announcement) {
      // Ensure ar field has proper structure when editing
      const arData = announcement.ar && Object.keys(announcement.ar).length > 0
        ? {
            ...announcement.ar,
            // Fill missing Arabic media_type if empty
            media_type: announcement.ar.media_type || arabicMediaTypeMap[announcement.media_type as MediaType] || '',
          }
        : {
            type: arabicTypeMap[announcement.type] || '',
            content: { title: '', description: '' },
            media_type: arabicMediaTypeMap[announcement.media_type as MediaType] || '',
          };

      setFormData({
        ...announcement,
        ar: arData,
      });
      if (announcement.media_url) {
        setImagePreview(announcement.media_url);
      }
    } else {
      // Set default dates
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 7);

      setFormData({
        type: 'info',
        media_type: 'text',
        content: { title: '', description: '' },
        ar: { type: 'معلومات', content: { title: '', description: '' }, media_type: 'نص' },
        start_at: now.toISOString().slice(0, 16),
        end_at: tomorrow.toISOString().slice(0, 16),
        priority: 0,
        is_active: true,
      });
      setImagePreview(null);
    }
  }, [announcement]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // Auto-populate Arabic type when type changes
      if (field === 'type') {
        updated.ar = {
          ...prev.ar,
          type: arabicTypeMap[value as AnnouncementType] || '',
          content: prev.ar?.content || { title: '', description: '' },
          media_type: prev.ar?.media_type || '',
        };
      }

      // Auto-populate Arabic media_type when media_type changes
      if (field === 'media_type') {
        updated.ar = {
          ...prev.ar,
          type: prev.ar?.type || '',
          content: prev.ar?.content || { title: '', description: '' },
          media_type: arabicMediaTypeMap[value as MediaType] || '',
        };
      }

      return updated;
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleContentChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        title: '',
        description: '',
        ...prev.content,
        [field]: value,
      },
    }));
  };

  const handleArContentChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      ar: {
        type: prev.ar?.type || '',
        content: {
          title: '',
          description: '',
          ...prev.ar?.content,
          [field]: value,
        },
        media_type: prev.ar?.media_type || '',
      },
    }));
  };

  const handleArTypeChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      ar: {
        ...prev.ar,
        type: value,
        content: prev.ar?.content || { title: '', description: '' },
        media_type: prev.ar?.media_type || '',
      },
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        media_url: t('announcements.errors.invalidImageType', 'Only JPEG, PNG, and WebP images are allowed'),
      }));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        media_url: t('announcements.errors.imageTooLarge', 'Image size must be less than 5MB'),
      }));
      return;
    }

    try {
      setUploading(true);
      setErrors((prev) => ({ ...prev, media_url: '' }));

      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;

      // Upload to Supabase Storage
      const url = await uploadToSupabaseStorage(file, fileName, {
        bucket: 'uploads',
        path: 'announcement',
        upsert: true,
      });

      // Update form data with the uploaded URL
      setFormData((prev) => ({
        ...prev,
        media_url: typeof url === 'string' ? url : url.publicUrl,
        
      }));

      // Set preview
      setImagePreview(typeof url === 'string' ? url : url.publicUrl);
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setErrors((prev) => ({
        ...prev,
        media_url: error.message || t('announcements.errors.uploadFailed', 'Failed to upload image'),
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      media_url: '',
    }));
    setImagePreview(null);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.content?.title) {
      newErrors.title = t('announcements.errors.titleRequired', 'Title is required');
    }

    if (!formData.start_at) {
      newErrors.start_at = t('announcements.errors.startDateRequired', 'Start date is required');
    }

    if (!formData.end_at) {
      newErrors.end_at = t('announcements.errors.endDateRequired', 'End date is required');
    }

    if (formData.start_at && formData.end_at && new Date(formData.start_at) >= new Date(formData.end_at)) {
      newErrors.end_at = t('announcements.errors.endDateAfterStart', 'End date must be after start date');
    }

    if (formData.media_type === 'image' && !formData.media_url) {
      newErrors.media_url = t('announcements.errors.mediaUrlRequired', 'Media URL is required for image type');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      console.log('Submitting announcement data:', formData);
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  const announcementTypes: { value: AnnouncementType; label: string }[] = [
    { value: 'info', label: t('announcements.types.info', 'Info') },
    { value: 'sale', label: t('announcements.types.sale', 'Sale') },
    { value: 'gift', label: t('announcements.types.gift', 'Gift') },
    { value: 'event', label: t('announcements.types.event', 'Event') },
    { value: 'promo', label: t('announcements.types.promo', 'Promo') },
  ];

  const mediaTypes: { value: MediaType; label: string }[] = [
    { value: 'text', label: t('announcements.mediaTypes.text', 'Text') },
    { value: 'image', label: t('announcements.mediaTypes.image', 'Image') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {announcement
              ? t('announcements.editTitle', 'Edit Announcement')
              : t('announcements.createTitle', 'Create Announcement')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('announcements.form.type', 'Type')}
            </label>
            <Select
              value={formData.type || 'info'}
              onChange={(value) => handleInputChange('type', value as AnnouncementType)}
              options={announcementTypes}
            />
          </div>

          {/* Media Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('announcements.form.mediaType', 'Media Type')}
            </label>
            <Select
              value={formData.media_type || 'text'}
              onChange={(value) => handleInputChange('media_type', value as MediaType)}
              options={mediaTypes}
            />
          </div>

          {/* Image Upload */}
          {formData.media_type === 'image' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('announcements.form.image', 'Image')}
              </label>
              
              {imagePreview ? (
                <div className="space-y-3">
                  <div className="relative w-full h-48 border-2 border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    <img
                      src={imagePreview}
                      alt="Announcement preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                 
                </div>
              ) : (
               
                <div className="space-y-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('announcements.form.uploading', 'Uploading...')}
                          </p>
                        </>
                      ) : (
                        <>
                          <FiUpload className="w-8 h-8 mb-2 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-semibold">{t('announcements.form.clickToUpload', 'Click to upload')}</span> {t('announcements.form.orDragDrop', 'or drag and drop')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG, WebP (MAX. 5MB)
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              )}
              
              {errors.media_url && (
                <p className="mt-1 text-sm text-red-600">{errors.media_url}</p>
              )}
            </div>
          )}
          {/* Title */}
          <InputField
            label={t('announcements.form.title', 'Title')}
            value={formData.content?.title || ''}
            onChange={(e) => handleContentChange('title', e.target.value)}
            placeholder={t('announcements.form.titlePlaceholder', 'Enter announcement title')}
            error={errors.title}
            required
          />

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('announcements.form.description', 'Description')}
            </label>
            <textarea
              value={formData.content?.description || ''}
              onChange={(e) => handleContentChange('description', e.target.value)}
              placeholder={t('announcements.form.descriptionPlaceholder', 'Enter description')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
            />
          </div>

          {/* Arabic Translation Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('announcements.form.arabicTranslation', 'Arabic Translation')}
            </h3>

            {/* Arabic Type (Auto-populated, Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('announcements.form.arabicType', 'Arabic Type')} ({t('common.autofilled', 'Auto-filled')})
              </label>
              <input
                type="text"
                value={formData.ar?.type || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none cursor-not-allowed"
                dir="rtl"
              />
            </div>

            {/* Arabic Title */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('announcements.form.arabicTitle', 'Arabic Title')}
              </label>
              <input
                type="text"
                value={formData.ar?.content?.title || ''}
                onChange={(e) => handleArContentChange('title', e.target.value)}
                placeholder={t('announcements.form.arabicTitlePlaceholder', 'Enter Arabic title')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                dir="rtl"
              />
            </div>

            {/* Arabic Description */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('announcements.form.arabicDescription', 'Arabic Description')}
              </label>
              <textarea
                value={formData.ar?.content?.description || ''}
                onChange={(e) => handleArContentChange('description', e.target.value)}
                placeholder={t('announcements.form.arabicDescriptionPlaceholder', 'Enter Arabic description')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
                dir="rtl"
              />
            </div>
          </div>

          {/* Discount (for sale type) */}
          {formData.type === 'sale' && (
            <InputField
              label={t('announcements.form.discount', 'Discount')}
              value={formData.content?.discount || ''}
              onChange={(e) => handleContentChange('discount', e.target.value)}
              placeholder="e.g., 20% Off"
            />
          )}

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('announcements.form.startDate', 'Start Date')}
            </label>
            <input
              type="datetime-local"
              value={formData.start_at ? new Date(formData.start_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleInputChange('start_at', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.start_at && <p className="mt-1 text-sm text-red-600">{errors.start_at}</p>}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('announcements.form.endDate', 'End Date')}
            </label>
            <input
              type="datetime-local"
              value={formData.end_at ? new Date(formData.end_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleInputChange('end_at', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {errors.end_at && <p className="mt-1 text-sm text-red-600">{errors.end_at}</p>}
          </div>

          {/* Priority */}
          <InputField
            label={t('announcements.form.priority', 'Priority')}
            type="number"
            value={formData.priority?.toString() || '0'}
            onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 0)}
            placeholder="0"
          />

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('announcements.form.isActive', 'Active')}
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 ">
            <Button type="button" onClick={onClose} variant="outline" className='cursor-pointer'>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" className='cursor-pointer'>
              {announcement
                ? t('common.update', 'Update')
                : t('common.create', 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementModal;
